'use client';
import { FC, useState, useMemo, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  TextField,
  Chip,
  Grid,
  Link,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { Print } from '@mui/icons-material';
import { createShippingSubmitValidation, getTrackingLink, convertDateForBackend } from './shippingUtils';
import { ShippingPrintableForm } from './ShippingPrintableForm';
import { ShippingSampleSelector } from './ShippingSampleSelector';
import { ShippingSampleRequestsTable } from './ShippingSampleRequestsTable';
import { ShippingTypeSelect } from './ShippingTypeSelect';
import { useBackend } from '@/hooks/useBackend';
import {
  ShippingDto,
  PrivilegeEnum,
  ShippingRequest,
  SampleWithLabel,
  SampleRequestWithDelivery,
  StatusDto,
} from '@/api/ApiClient';

import { formatDate } from '@/lib/utils/dateUtils';
import { Footer } from '@/components/details/Footer';
import DetailField from '@/components/common/DetailField';
import { useSelectOptions } from '@/lib/options/useSelectOptions';
import { OptionKey } from '@/lib/options/optionTypes';
import { useTree } from '@/hooks/useTree';
import { useTreeSelection } from '@/hooks/useNodeState';
import { useIsDataModified, useShippingData } from '@/hooks/useTreeDetailSubscriptions';
import { useSnackbar } from '@/hooks/useSnackbar';
import { log } from '@/lib/utils/logger';
import { getProblemDetail } from '@/lib/utils/apiErrors';
import { useSubmitWithValidation } from '@/hooks/useSubmitWithValidation';
import { usePrivileges } from '@/hooks/usePrivileges';
import { RichTextEditor } from '@/components/common/RichTextEditor';

import './ShippingDetails.css';

export const selectOptions: OptionKey[] = [
  'researcherOptions',
  'facultyOptions',
  'sampleRequestStatus',
  'shippingLabelTypes',
  'shippingTypes',
];

const ShippingDetails: FC = () => {
  const { options, loading: optionsLoading, error: optionsError } = useSelectOptions(selectOptions);
  const backend = useBackend();
  const { showSnackbar } = useSnackbar();

  const { setDataModified, updateDataPartial, updateData, getSampleRequests, setSampleRequests } = useTree();
  const { selectedNode } = useTreeSelection();
  const { submitWithValidation } = useSubmitWithValidation();

  const [isManuallyEditing, setIsManuallyEditing] = useState(false);
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const shipping = useShippingData(selectedNode);

  // Extract projectLabel and projectId for lookups
  const projectId = shipping?.projectId;
  const projectLabel = shipping?.projectLabel;

  // Get sample requests from tree store, load if not already loaded
  const sampleRequests = useMemo(() => {
    return projectId ? getSampleRequests(projectId) || [] : [];
  }, [projectId, getSampleRequests]);

  // Load sample requests if not already loaded
  useEffect(() => {
    if (!projectId || !projectLabel) return;

    // Check if already loaded
    const cached = getSampleRequests(projectId);
    if (cached) return;

    // Load sample requests
    let cancelled = false;
    const loadSampleRequests = async () => {
      try {
        const requests = await backend.project.getSampleRequests(projectLabel);
        if (!cancelled) {
          setSampleRequests(projectId, requests);
          log.debug('[ShippingDetails] Loaded sample requests:', { projectId, count: requests.length });
        }
      } catch (error) {
        if (!cancelled) {
          log.error('[ShippingDetails] Failed to load sample requests:', error);
        }
      }
    };
    loadSampleRequests();

    return () => {
      cancelled = true;
    };
  }, [projectId, projectLabel, backend, getSampleRequests, setSampleRequests]);

  // Get label types from options (loaded globally via useSelectOptions)
  const availableLabelTypes = options.shippingLabelTypes || [];
  const availableShippingTypes = options.shippingTypes || [];

  const privilege = PrivilegeEnum.Shipping;
  const { isAuthorized } = usePrivileges('project', shipping?.projectLabel);

  // Compute isReadOnly during render - derived from modification state and manual edit mode
  const dataId = selectedNode?.dataId;
  const isModified = useIsDataModified(dataId);
  const isReadOnly = !isModified && !isManuallyEditing;

  const getRealUserName = (userId: string) =>
    options.researcherOptions?.find((k) => k.key === userId)?.value ||
    options.facultyOptions?.find((k) => k.key === userId)?.value ||
    userId;

  const handleModification = (modified: Partial<ShippingDto>) => {
    if (!selectedNode) return;
    updateDataPartial(selectedNode.dataId, modified);
  };

  const handleSampleRequestToggle = (requestId: number, relevant: boolean) => {
    if (!shipping) return;
    const currentRequests = shipping.sampleRequests || [];
    const existing = currentRequests.find((r) => r.requestId === requestId);
    if (relevant && !existing) {
      handleModification({
        sampleRequests: [...currentRequests, { requestId, partialDelivery: false }],
      });
    } else if (!relevant && existing) {
      handleModification({
        sampleRequests: currentRequests.filter((r) => r.requestId !== requestId),
      });
    }
  };

  const handlePartialDeliveryChange = (requestId: number, partialDelivery: boolean) => {
    if (!shipping) return;
    const currentRequests = shipping.sampleRequests || [];
    handleModification({
      sampleRequests: currentRequests.map((r) => (r.requestId === requestId ? { ...r, partialDelivery } : r)),
    });
  };

  // Get visible request lines (requests that are linked or can be linked)
  const visibleRequestLines = useMemo(() => {
    if (!shipping || !sampleRequests.length) return [];
    const linkedRequestIds = new Set((shipping.sampleRequests || []).map((r) => r.requestId));
    return sampleRequests.filter((req) => linkedRequestIds.has(req.id!) || req.requestStatus !== 'C');
  }, [shipping, sampleRequests]);

  // Convert ShippingDto to ShippingRequest (strip out full sample/request objects)
  const convertToRequest = (dto: ShippingDto): ShippingRequest => {
    return {
      projectId: dto.projectId,
      researcher: dto.researcher,
      researcherPhone: dto.researcherPhone,
      researcherEmail: dto.researcherEmail,
      address: dto.address,
      returnAddress: dto.returnAddress,
      date: dto.date,
      budget: dto.budget,
      contactName: dto.contactName,
      contactEmail: dto.contactEmail,
      contactPhone: dto.contactPhone,
      localTransfer: dto.localTransfer,
      shippingType: dto.shippingType,
      trackingID: dto.trackingID,
      comment: dto.comment,
      status: dto.status,
      samples: (dto.samples || []).map((s): SampleWithLabel => ({
        sampleId: s.sampleId,
        labelType: s.labelType,
      })),
      sampleRequests: (dto.sampleRequests || []).map((r): SampleRequestWithDelivery => ({
        requestId: r.requestId,
        partialDelivery: r.partialDelivery ?? false,
      })),
    };
  };

  const handleSave = async () => {
    if (!shipping?.id || !selectedNode) return;
    try {
      const request = convertToRequest(shipping);
      const updatedData = await backend.shipping.update(shipping.id, request);
      // Update the data in the store
      updateData(selectedNode.dataId, updatedData);
      setDataModified(selectedNode.dataId, false);
      setIsManuallyEditing(false); // Exit edit mode after save
      showSnackbar('Shipping details saved successfully', 'success');
    } catch (error) {
      log.error('[ShippingDetails] Failed to save shipping:', error);
      showSnackbar(getProblemDetail(error) ?? 'Failed to save shipping details', 'error');
    }
  };

  // Complete shipping - mark as shipped with date and tracking number
  const handleCompleteShipping = async () => {
    if (!shipping?.id || !selectedNode) return;

    // Validate required fields for completing shipping
    const errors: Record<string, string> = {};
    if (!shipping.date) {
      errors.date = 'Shipping date is required';
    }
    if (!shipping.trackingID || shipping.trackingID.trim() === '') {
      errors.trackingID = 'Tracking number is required';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setIsManuallyEditing(true);
      showSnackbar('Please fill in all required fields', 'error');
      return;
    }

    try {
      // Save any pending changes first
      if (isModified) {
        const request = convertToRequest(shipping);
        await backend.shipping.update(shipping.id, request);
      }

      // Mark as shipped
      const updatedData = await backend.shipping.shipped(shipping.id);
      updateData(selectedNode.dataId, updatedData);
      setDataModified(selectedNode.dataId, false);
      setIsManuallyEditing(false);
      setFieldErrors({});
      showSnackbar('Shipping marked as complete', 'success');
    } catch (error) {
      log.error('[ShippingDetails] Failed to complete shipping:', error);
      showSnackbar(getProblemDetail(error) ?? 'Failed to complete shipping', 'error');
    }
  };

  // Custom submit handler with validation
  const handleSubmit = async (): Promise<boolean> => {
    if (!shipping || !selectedNode) return false;
    const validationFields = createShippingSubmitValidation(shipping);
    const result = await submitWithValidation(selectedNode, validationFields, 'Shipping');
    if (!result.success && result.fieldErrors) {
      // Set field errors to highlight invalid fields
      setFieldErrors(result.fieldErrors);
      // Switch to edit mode so user can see and fix the errors
      setIsManuallyEditing(true);
    } else {
      // Clear field errors on successful submit
      setFieldErrors({});
    }
    return result.success;
  };

  // Clear field errors when user starts editing a field
  const clearFieldError = (fieldKey: string) => {
    if (fieldErrors[fieldKey]) {
      setFieldErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldKey];
        return newErrors;
      });
    }
  };

  if (optionsLoading || !options.researcherOptions || !options.facultyOptions || !options.sampleRequestStatus) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" color="text.secondary">
          {optionsError ? `Error loading options: ${optionsError.message}` : 'Loading options...'}
        </Typography>
      </Box>
    );
  }

  if (!shipping || selectedNode?.type !== 'shipping') {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="body1" color="text.secondary">
          {!shipping ? 'Select a node to view details' : 'Selected node is not a shipping.'}
        </Typography>
      </Box>
    );
  }

  const isSubmitted = shipping.status === StatusDto.Submitted;
  const isLocalTransfer = shipping.localTransfer;
  const isPending = shipping.status === StatusDto.Pending;
  const isShipped = shipping.shipped ?? false;

  // Users with Shipping Execute privilege can add shipping info (date, tracking) for submitted but not yet shipped items
  const canCompleteShipping = isSubmitted && !isShipped && isAuthorized(PrivilegeEnum.Shipping, 'execute');

  const handlePrint = () => {
    setShowPrintDialog(true);
  };

  const handlePrintDialogClose = () => {
    setShowPrintDialog(false);
  };

  const handlePrintExecute = () => {
    window.print();
  };

  return (
    <>
      {/* Regular view */}
      <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>
        <Box sx={{ flex: 1, overflow: 'auto', minHeight: 0, p: 2 }}>
          <Stack spacing={3} className="shipping-regular-view no-print">
            <Box className="shipping-header">
              <Box className="shipping-header-left">
                {isLocalTransfer ? <Chip label="Local Transfer" color="primary" size="small" /> : null}
              </Box>
              {!isLocalTransfer ? (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Print />}
                  onClick={handlePrint}
                  className="shipping-print-button no-print"
                  sx={{
                    display: { xs: 'none', sm: 'flex' },
                  }}
                >
                  Printable View
                </Button>
              ) : null}
            </Box>

            <Paper elevation={0} variant="outlined" sx={{ p: 2 }}>
              <Stack spacing={3}>
                {/* Top section - General Information */}
                {isLocalTransfer ? (
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 3 }}>
                      <DetailField
                        label="Project"
                        value={shipping.projectLabel ?? 'N/A'}
                        isReadOnly={true}
                        width={{ width: '100%' }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DetailField
                          label="Transfer Date"
                          value={shipping.date ? formatDate(shipping.date) : 'N/A'}
                          isReadOnly={isReadOnly || isSubmitted}
                          width={{ width: '100%' }}
                          requiredToSubmit
                          error={fieldErrors.date}
                        >
                          <DatePicker
                            value={shipping.date ? new Date(shipping.date) : null}
                            onChange={(newValue) => {
                              clearFieldError('date');
                              handleModification({
                                date: convertDateForBackend(newValue),
                              });
                            }}
                            slotProps={{
                              textField: {
                                fullWidth: true,
                                size: 'small',
                                error: !!fieldErrors.date,
                                helperText: fieldErrors.date,
                              },
                            }}
                          />
                        </DetailField>
                      </LocalizationProvider>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <DetailField
                        label="Contact (if not PI of the project)"
                        value={shipping.contactName ?? 'N/A'}
                        isReadOnly={isReadOnly || isSubmitted}
                        width={{ width: '100%' }}
                      >
                        <TextField
                          value={shipping.contactName ?? ''}
                          onChange={(e) => handleModification({ contactName: e.target.value })}
                          fullWidth
                          size="small"
                        />
                      </DetailField>
                    </Grid>
                  </Grid>
                ) : (
                  <>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <DetailField
                          label="Project"
                          value={shipping.projectLabel ?? 'N/A'}
                          isReadOnly={true}
                          width={{ width: '100%' }}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 7 }}>
                        <DetailField
                          label="Internal Order Number"
                          value={shipping.budget ?? 'N/A'}
                          isReadOnly={isReadOnly || isSubmitted}
                          width={{ width: '100%' }}
                        >
                          <TextField
                            value={shipping.budget ?? ''}
                            onChange={(e) => handleModification({ budget: e.target.value })}
                            fullWidth
                            size="small"
                          />
                        </DetailField>
                      </Grid>
                    </Grid>
                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <DetailField
                          label="Shipping Type"
                          value={shipping.shippingType ?? 'N/A'}
                          isReadOnly={isReadOnly || isSubmitted}
                          width={{ width: '100%' }}
                          requiredToSubmit
                          error={fieldErrors.shippingType}
                        >
                          <ShippingTypeSelect
                            value={shipping.shippingType ?? ''}
                            options={availableShippingTypes}
                            onChange={(shippingType) => {
                              clearFieldError('shippingType');
                              handleModification({ shippingType });
                            }}
                            error={!!fieldErrors.shippingType}
                            helperText={fieldErrors.shippingType}
                          />
                        </DetailField>
                      </Grid>
                      <Grid size={{ xs: 12, md: 3 }}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                          <DetailField
                            label="Shipping Date"
                            value={shipping.date ? formatDate(shipping.date) : 'N/A'}
                            isReadOnly={isReadOnly || (isSubmitted && !canCompleteShipping)}
                            width={{ width: '100%' }}
                            requiredToSubmit={canCompleteShipping}
                            error={fieldErrors.date}
                          >
                            <DatePicker
                              value={shipping.date ? new Date(shipping.date) : null}
                              onChange={(newValue) => {
                                clearFieldError('date');
                                handleModification({
                                  date: convertDateForBackend(newValue),
                                });
                              }}
                              slotProps={{
                                textField: {
                                  fullWidth: true,
                                  size: 'small',
                                  error: !!fieldErrors.date,
                                  helperText: fieldErrors.date,
                                },
                              }}
                            />
                          </DetailField>
                        </LocalizationProvider>
                      </Grid>
                      <Grid size={{ xs: 12, md: 4 }}>
                        <DetailField
                          label="Tracking Number"
                          value={shipping.trackingID ?? 'N/A'}
                          isReadOnly={isReadOnly || (isSubmitted && !canCompleteShipping)}
                          width={{ width: '100%' }}
                          requiredToSubmit={canCompleteShipping}
                          error={fieldErrors.trackingID}
                        >
                          <TextField
                            value={shipping.trackingID ?? ''}
                            onChange={(e) => {
                              clearFieldError('trackingID');
                              handleModification({ trackingID: e.target.value });
                            }}
                            fullWidth
                            size="small"
                            error={!!fieldErrors.trackingID}
                            helperText={fieldErrors.trackingID}
                          />
                        </DetailField>
                      </Grid>
                      <Grid size={{ xs: 12, md: 2 }}>
                        <Box sx={{ pt: 4.5 }}>
                          {shipping.trackingID && getTrackingLink(shipping.trackingID, shipping.shippingType) ? (
                            <Link
                              href={getTrackingLink(shipping.trackingID, shipping.shippingType)}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="shipping-tracking-link"
                            >
                              Track Shipping
                            </Link>
                          ) : null}
                        </Box>
                      </Grid>
                    </Grid>
                  </>
                )}

                {/* Two-column layout for Sender and Recipient */}
                <Box className="shipping-panels-container">
                  {/* Sender Panel */}
                  <Paper elevation={0} variant="outlined" className="shipping-panel shipping-paper">
                    <Typography variant="subtitle1" gutterBottom>
                      Sender
                    </Typography>
                    <Stack spacing={2} className="shipping-panel-content">
                      <DetailField
                        label="Researcher"
                        value={shipping.researcher ?? 'N/A'}
                        isReadOnly={true}
                        width={{ width: '100%' }}
                      />
                      <DetailField
                        label="Email"
                        value={shipping.researcherEmail ?? 'N/A'}
                        isReadOnly={true}
                        width={{ width: '100%' }}
                      />
                      {/* Phone and Return Address only shown for regular shipping, not local transfers */}
                      {!isLocalTransfer ? (
                        <>
                          <DetailField
                            label="Phone"
                            value={shipping.researcherPhone ?? 'N/A'}
                            isReadOnly={isReadOnly || isSubmitted}
                            width={{ width: '100%' }}
                            requiredToSubmit
                            error={fieldErrors.researcherPhone}
                          >
                            <TextField
                              value={shipping.researcherPhone ?? ''}
                              onChange={(e) => {
                                clearFieldError('researcherPhone');
                                handleModification({ researcherPhone: e.target.value });
                              }}
                              fullWidth
                              size="small"
                              error={!!fieldErrors.researcherPhone}
                              helperText={fieldErrors.researcherPhone}
                            />
                          </DetailField>
                          <DetailField
                            label="Return Address"
                            value={shipping.returnAddress ?? 'N/A'}
                            isReadOnly={isReadOnly || isSubmitted}
                            width={{ width: '100%' }}
                            requiredToSubmit
                            error={fieldErrors.returnAddress}
                          >
                            <TextField
                              value={shipping.returnAddress ?? ''}
                              onChange={(e) => {
                                clearFieldError('returnAddress');
                                handleModification({ returnAddress: e.target.value });
                              }}
                              fullWidth
                              multiline
                              rows={4}
                              size="small"
                              error={!!fieldErrors.returnAddress}
                              helperText={fieldErrors.returnAddress}
                            />
                          </DetailField>
                        </>
                      ) : null}
                    </Stack>
                  </Paper>

                  {/* Recipient Panel - only shown for regular shipping, not local transfers */}
                  {!isLocalTransfer ? (
                    <Paper elevation={0} variant="outlined" className="shipping-panel shipping-paper">
                      <Typography variant="subtitle1" gutterBottom>
                        Ship To
                      </Typography>
                      <Stack spacing={2} className="shipping-panel-content">
                        <DetailField
                          label="Contact Name"
                          value={shipping.contactName ?? 'N/A'}
                          isReadOnly={isReadOnly || isSubmitted}
                          width={{ width: '100%' }}
                          requiredToSubmit
                        >
                          <TextField
                            value={shipping.contactName ?? ''}
                            onChange={(e) => {
                              clearFieldError('contactName');
                              handleModification({ contactName: e.target.value });
                            }}
                            fullWidth
                            size="small"
                            error={!!fieldErrors.contactName}
                            helperText={fieldErrors.contactName}
                          />
                        </DetailField>
                        <DetailField
                          label="Contact Email"
                          value={shipping.contactEmail ?? 'N/A'}
                          isReadOnly={isReadOnly || isSubmitted}
                          width={{ width: '100%' }}
                          requiredToSubmit
                          error={fieldErrors.contactEmail}
                        >
                          <TextField
                            value={shipping.contactEmail ?? ''}
                            onChange={(e) => {
                              clearFieldError('contactEmail');
                              handleModification({ contactEmail: e.target.value });
                            }}
                            fullWidth
                            size="small"
                            error={!!fieldErrors.contactEmail}
                            helperText={fieldErrors.contactEmail}
                          />
                        </DetailField>
                        <DetailField
                          label="Contact Phone"
                          value={shipping.contactPhone ?? 'N/A'}
                          isReadOnly={isReadOnly || isSubmitted}
                          width={{ width: '100%' }}
                          requiredToSubmit
                          error={fieldErrors.contactPhone}
                        >
                          <TextField
                            value={shipping.contactPhone ?? ''}
                            onChange={(e) => {
                              clearFieldError('contactPhone');
                              handleModification({ contactPhone: e.target.value });
                            }}
                            fullWidth
                            size="small"
                            error={!!fieldErrors.contactPhone}
                            helperText={fieldErrors.contactPhone}
                          />
                        </DetailField>
                        <DetailField
                          label="Shipping Address"
                          value={shipping.address ?? 'N/A'}
                          isReadOnly={isReadOnly || isSubmitted}
                          width={{ width: '100%' }}
                          requiredToSubmit
                          error={fieldErrors.address}
                        >
                          <TextField
                            value={shipping.address ?? ''}
                            onChange={(e) => {
                              clearFieldError('address');
                              handleModification({ address: e.target.value });
                            }}
                            fullWidth
                            multiline
                            rows={4}
                            size="small"
                            error={!!fieldErrors.address}
                            helperText={fieldErrors.address}
                          />
                        </DetailField>
                      </Stack>
                    </Paper>
                  ) : null}
                </Box>

                {/* Comment (richtext) */}
                <Box>
                  <DetailField
                    label="Comment"
                    value={
                      isReadOnly || isSubmitted ? (
                        shipping.comment ? (
                          <Box
                            component="span"
                            sx={{ display: 'block', minHeight: 24, py: 0.5 }}
                            dangerouslySetInnerHTML={{ __html: shipping.comment }}
                          />
                        ) : (
                          'N/A'
                        )
                      ) : (
                        ''
                      )
                    }
                    isReadOnly={isReadOnly || isSubmitted}
                    width={{ width: '100%' }}
                  >
                    <RichTextEditor
                      value={shipping.comment ?? ''}
                      onChange={(html) => handleModification({ comment: html || undefined })}
                    />
                  </DetailField>
                </Box>
              </Stack>
            </Paper>

            {/* Samples Table */}
            <ShippingSampleSelector
              projectLabel={shipping.projectLabel ?? ''}
              samples={shipping.samples ?? []}
              onSamplesChange={(samples) => handleModification({ samples })}
              isLocalTransfer={isLocalTransfer ?? false}
              isPending={isPending}
              isSubmitted={isSubmitted}
              isReadOnly={isReadOnly}
              availableLabelTypes={availableLabelTypes}
              requiredToSubmit
              errorMessage={fieldErrors.samples}
              onSamplesModified={() => clearFieldError('samples')}
            />

            {/* Sample Requests Table */}
            <ShippingSampleRequestsTable
              shipping={shipping}
              visibleRequestLines={visibleRequestLines}
              isSubmitted={isSubmitted}
              isReadOnly={isReadOnly}
              requestStatusOptions={options.sampleRequestStatus}
              onRequestToggle={handleSampleRequestToggle}
              onPartialDeliveryChange={handlePartialDeliveryChange}
            />

            {/* Complete Shipping Button - shown when submitted but not yet shipped */}
            {canCompleteShipping ? (
              <Paper elevation={0} variant="outlined" sx={{ p: 2, mt: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <Box>
                    <Typography variant="subtitle1" gutterBottom>
                      Complete Shipping
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Add shipping date and tracking number to mark this shipping as complete.
                    </Typography>
                  </Box>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleCompleteShipping}
                    disabled={!shipping.date || !shipping.trackingID}
                  >
                    Mark as Shipped
                  </Button>
                </Box>
              </Paper>
            ) : null}
          </Stack>
        </Box>

        {/* Footer - Fixed at bottom */}
        <Box sx={{ flexShrink: 0 }}>
          <Footer
            lastChangedDate={shipping.lastChangedDate}
            lastChangedBy={getRealUserName(shipping.lastChangedByUser || '')}
            status={shipping.status}
            isReadOnly={isReadOnly}
            setIsReadOnly={(val) => setIsManuallyEditing(!val)}
            onSave={handleSave}
            privilege={privilege}
            onSubmit={handleSubmit}
          />
        </Box>
      </Box>

      {/* Print Dialog */}
      <Dialog
        open={showPrintDialog}
        onClose={handlePrintDialogClose}
        maxWidth="lg"
        fullWidth
        className="shipping-print-dialog"
        PaperProps={{
          className: 'shipping-print-dialog-paper',
        }}
      >
        <DialogContent className="shipping-print-dialog-content">
          {shipping ? <ShippingPrintableForm shipping={shipping} isLocalTransfer={isLocalTransfer ?? false} /> : null}
        </DialogContent>
        <DialogActions className="shipping-print-dialog-actions">
          <Button onClick={handlePrintDialogClose}>Close</Button>
          <Button variant="contained" onClick={handlePrintExecute} startIcon={<Print />}>
            Print
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ShippingDetails;
